import React from 'react';

import ListControls from '@sharedCR/common/ListControls';
import { compose } from 'redux';

import { TODAY_IN_DAYS } from 'constants/date';
import { FieldType } from 'constants/formGenerator';
import {
  borrowerHomeownershipEducationFormatOptions,
  borrowerHousingCounselingFormatOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../../Borrowers.testIds';
import { defaultRadioInputProps } from '../Declarations.constants';
import {
  radioListStyles,
  sectionStyles,
  sectionTitleStyles,
  textFieldStyles,
} from '../Declarations.styles';

import AgencyIdNumberTitle from './AgencyIdNumberTitle';
import {
  agencyPreviewInputProps,
  CounselingAgencyType,
} from './HomeownershipEducationAndHousingCounseling.constants';

export const getModelPath = entityId =>
  `[${entityId}].declarations.homeownershipEducationAndHousingCounseling.`;

export const getConfigFields = ({ entityId }) => {
  const modelPath = getModelPath(entityId);

  return {
    isBorrowerCompletedHomeownershipEducation: {
      fieldId: 'isBorrowerCompletedHomeownershipEducation',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isBorrowerCompletedHomeownershipEducation`,
      inputProps: {
        label:
          'Has the Borrower(s) completed homeownership education (group or web-based classes) within the last 12 months?',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_IS_BORROWER_COMPLETED_HOMEOWNERSHIP_EDUCATION_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    borrowerHomeownershipEducationFormat: {
      fieldId: 'borrowerHomeownershipEducationFormat',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}borrowerHomeownershipEducationFormat`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_BORROWER_HOMEOWNERSHIP_EDUCATION_FORMAT_SELECT,
        label: 'What format was it in: (Check the most recent)',
        ...defaultRadioInputProps,
        options: borrowerHomeownershipEducationFormatOptions,
        radioGroupAlign: ItemGroupAlignment.VERTICAL,
        align: ItemGroupAlignment.VERTICAL,
        labelPlacement: ItemLabelPlacement.END,
        required: true,
        customStyles: {
          ...radioListStyles,
          alignItems: 'start',
        },
      },
    },
    homeownershipEducationHousingCounselingAgencyIdNumber: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyIdNumber',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyIdNumber`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_ID_NUMBER_INPUT,
        placeholder: 'Provide Housing Counseling Agency ID Number',
        className: 'w-full',
        label: 'Housing Counseling Agency ID Number',
        required: true,
        maxLength: 100,
        customStyles: textFieldStyles,
        title: <AgencyIdNumberTitle />,
      },
    },
    homeownershipEducationHousingCounselingAgencyName: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyName',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyName`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'Name',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_NAME_INPUT,
      },
    },
    homeownershipEducationHousingCounselingAgencyState: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyState',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyState`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'State',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_STATE_INPUT,
      },
    },
    homeownershipEducationHousingCounselingAgencyCity: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyCity',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyCity`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'City',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_CITY_INPUT,
      },
    },
    homeownershipEducationHousingCounselingAgencyAddress1: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyAddress1',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyAddress1`,
      inputProps: {
        ...agencyPreviewInputProps,
        required: false,
        label: 'Address 1',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_ADDRESS_1_INPUT,
      },
    },
    homeownershipEducationHousingCounselingAgencyAddress2: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyAddress2',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyAddress2`,
      inputProps: {
        ...agencyPreviewInputProps,
        required: false,
        label: 'Address 2',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_ADDRESS_2_INPUT,
      },
    },
    homeownershipEducationHousingCounselingAgencyZip: {
      fieldId: 'homeownershipEducationHousingCounselingAgencyZip',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationHousingCounselingAgencyZip`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'Zip Code',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_ZIP_INPUT,
      },
    },
    homeownershipEducationNameOfTheHousingEducationProgram: {
      fieldId: 'homeownershipEducationNameOfTheHousingEducationProgram',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}homeownershipEducationNameOfTheHousingEducationProgram`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_NAME_OF_THE_HOUSING_EDUCATION_PROGRAM_INPUT,
        placeholder: 'Provide the name of the Housing Education Program',
        label: 'Name of the Housing Education Program',
        required: true,
        maxLength: 100,
        title:
          'If not a HUD-approved agency, or unsure of HUD approval, provide the name of the Housing Education Program:',
        containerClassName: 'pb-8',
        customStyles: textFieldStyles,
      },
    },
    dateOfCompletionHomeownershipEducation: {
      fieldId: 'dateOfCompletionHomeownershipEducation',
      type: FieldType.DATE,
      nameInModel: `${modelPath}dateOfCompletionHomeownershipEducation`,
      inputProps: {
        dataTestId: BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_DATE_OF_COMPLETION_INPUT,
        placeholder: 'Date of completion',
        className: 'w-full',
        customStyles: textFieldStyles,
        label: 'Date of completion',
        maxDate: TODAY_IN_DAYS,
        required: true,
        containerClassName: 'pb-8',
      },
    },
    isBorrowerCompletedHousingCounseling: {
      fieldId: 'isBorrowerCompletedHousingCounseling',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isBorrowerCompletedHousingCounseling`,
      inputProps: {
        label:
          'Has the Borrower(s) completed housing counseling (customized counselor-to-client services) within the last 12 months?',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_IS_BORROWER_COMPLETED_HOUSING_COUNSELING_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    borrowerHousingCounselingFormat: {
      fieldId: 'borrowerHousingCounselingFormat',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}borrowerHousingCounselingFormat`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_BORROWER_HOUSING_COUNSELING_FORMAT_SELECT,
        label: 'What format was it in: (Check the most recent)',
        ...defaultRadioInputProps,
        required: true,
        options: borrowerHousingCounselingFormatOptions,
        align: ItemGroupAlignment.VERTICAL,
        radioGroupAlign: ItemGroupAlignment.VERTICAL,
        labelPlacement: ItemLabelPlacement.END,
        customStyles: {
          ...radioListStyles,
          alignItems: 'start',
        },
      },
    },
    // field and nameInModel are different because of Loan History Changes
    housingCounselingAgencyIdNumber: {
      fieldId: 'housingCounselingAgencyIdNumber',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyIdNumber`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_ID_NUMBER_INPUT,
        placeholder: 'Provide Housing Counseling Agency ID Number',
        label: 'Housing Counseling Agency ID Number',
        maxLength: 100,
        required: true,
        disabled: true,
        customStyles: textFieldStyles,
        title: <AgencyIdNumberTitle />,
      },
    },
    housingCounselingAgencyName: {
      fieldId: 'housingCounselingAgencyName',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyName`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'Name',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_NAME_INPUT,
      },
    },
    housingCounselingAgencyState: {
      fieldId: 'housingCounselingAgencyState',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyState`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'State',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_STATE_INPUT,
      },
    },
    housingCounselingAgencyCity: {
      fieldId: 'housingCounselingAgencyCity',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyCity`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'City',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_CITY_INPUT,
      },
    },
    housingCounselingAgencyAddress1: {
      fieldId: 'housingCounselingAgencyAddress1',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyAddress1`,
      inputProps: {
        ...agencyPreviewInputProps,
        required: false,
        label: 'Address 1',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_ADDRESS_1_INPUT,
      },
    },
    housingCounselingAgencyAddress2: {
      fieldId: 'housingCounselingAgencyAddress2',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyAddress2`,
      inputProps: {
        ...agencyPreviewInputProps,
        required: false,
        label: 'Address 2',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_ADDRESS_2_INPUT,
      },
    },
    housingCounselingAgencyZip: {
      fieldId: 'housingCounselingAgencyZip',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingAgencyZip`,
      inputProps: {
        ...agencyPreviewInputProps,
        label: 'Zip Code',
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_ZIP_INPUT,
      },
    },
    housingCounselingNameOfTheHousingEducationProgram: {
      fieldId: 'housingCounselingNameOfTheHousingEducationProgram',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}housingCounselingNameOfTheHousingEducationProgram`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_NAME_OF_THE_HOUSING_EDUCATION_PROGRAM_INPUT,
        placeholder: 'Provide the name of the Housing Education Program',
        label: 'Name of the Housing Education Program',
        required: true,
        title:
          'If not a HUD-approved agency, or unsure of HUD approval, provide the name of the Housing Education Program:',
        containerClassName: 'pb-8',
        customStyles: textFieldStyles,
      },
    },
    dateOfCompletionHousingCounseling: {
      fieldId: 'dateOfCompletionHousingCounseling',
      type: FieldType.DATE,
      nameInModel: `${modelPath}dateOfCompletionHousingCounseling`,
      inputProps: {
        dataTestId:
          BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_DATE_OF_COMPLETION_HOUSING_COUNSELING_INPUT,
        placeholder: 'Date of completion',
        containerClassName: 'pb-8',
        label: 'Date of completion',
        customStyles: textFieldStyles,
        maxDate: TODAY_IN_DAYS,
        required: true,
      },
    },
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    isBorrowerCompletedHomeownershipEducation,
    isBorrowerCompletedHousingCounseling,
    isHomeownershipEducationNameOfTheHousingEducationProgram,
    isHousingCounselingNameOfTheHousingEducationProgram,
    onRemoveCounselingAgency,
    onAddCounselingAgency,
  }) => {
    const configFields = getConfigFields({ entityId });

    const handleRemoveAgency = agencyType => () => onRemoveCounselingAgency(agencyType);
    const handleAddAgency = agencyType => () => onAddCounselingAgency(agencyType);

    const getAddRemoveAgencyButton = (agencyType, isSelectedAgency) => {
      return (
        <ListControls
          isAddVisible={!isSelectedAgency}
          isRemoveVisible={isSelectedAgency}
          onAdd={handleAddAgency(agencyType)}
          onRemove={handleRemoveAgency(agencyType)}
        />
      );
    };

    return {
      groups: [
        {
          title: {
            label: 'Homeownership Education and Housing Counseling',
            titleStyles: sectionTitleStyles,
          },
          key: `${entityId}-declarations-homeownership-education-and-housing-counseling`,
          customStyles: sectionStyles,
          type: FieldType.GROUP,
          fields: [
            configFields.isBorrowerCompletedHomeownershipEducation,
            ...getConditionalArray(isBorrowerCompletedHomeownershipEducation, () => [
              configFields.borrowerHomeownershipEducationFormat,
              {
                title: {
                  label: 'Homeownership Education Housing Counseling Agency',
                  titleStyles: sectionTitleStyles,
                },
                customStyles: { gridTemplateColumns: `repeat(4, 1fr)` },
                type: FieldType.GROUP,
                customControls: getAddRemoveAgencyButton(
                  CounselingAgencyType.EDUCATION_COUNSELING_AGENCY,
                  isHomeownershipEducationNameOfTheHousingEducationProgram,
                ),
                fields: [
                  {
                    ...configFields.homeownershipEducationHousingCounselingAgencyIdNumber,
                    inputProps: {
                      ...agencyPreviewInputProps,
                      label: 'Id',
                      dataTestId:
                        BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOMEOWNERSHIP_EDUCATION_HOUSING_COUNSELING_AGENCY_ID_INPUT,
                      placeholder: 'Id',
                    },
                  },
                  configFields.homeownershipEducationHousingCounselingAgencyName,
                  configFields.homeownershipEducationHousingCounselingAgencyState,
                  configFields.homeownershipEducationHousingCounselingAgencyCity,
                  configFields.homeownershipEducationHousingCounselingAgencyAddress1,
                  configFields.homeownershipEducationHousingCounselingAgencyAddress2,
                  configFields.homeownershipEducationHousingCounselingAgencyZip,
                ],
              },
              {
                ...configFields.homeownershipEducationNameOfTheHousingEducationProgram,
                inputProps: {
                  ...configFields.homeownershipEducationNameOfTheHousingEducationProgram.inputProps,
                  required: !isHomeownershipEducationNameOfTheHousingEducationProgram,
                  disabled: isHomeownershipEducationNameOfTheHousingEducationProgram,
                },
              },
              configFields.dateOfCompletionHomeownershipEducation,
            ]),
            configFields.isBorrowerCompletedHousingCounseling,
            ...getConditionalArray(isBorrowerCompletedHousingCounseling, () => [
              configFields.borrowerHousingCounselingFormat,
              {
                title: {
                  label: 'Housing Counseling Agency',
                  titleStyles: sectionTitleStyles,
                },
                type: FieldType.GROUP,
                customStyles: { gridTemplateColumns: `repeat(4, 1fr)` },
                customControls: getAddRemoveAgencyButton(
                  CounselingAgencyType.COUNSELING_AGENCY,
                  isHousingCounselingNameOfTheHousingEducationProgram,
                ),
                fields: [
                  {
                    ...configFields.housingCounselingAgencyIdNumber,
                    inputProps: {
                      ...agencyPreviewInputProps,
                      label: 'Id',
                      dataTestId:
                        BorrowersTestIds.HOMEOWNERSHIP_EDUCATION_FORM_HOUSING_COUNSELING_AGENCY_ID_INPUT,
                      placeholder: 'id',
                    },
                  },
                  configFields.housingCounselingAgencyName,
                  configFields.housingCounselingAgencyState,
                  configFields.housingCounselingAgencyCity,
                  configFields.housingCounselingAgencyAddress1,
                  configFields.housingCounselingAgencyAddress2,
                  configFields.housingCounselingAgencyZip,
                ],
              },
              {
                ...configFields.housingCounselingNameOfTheHousingEducationProgram,
                inputProps: {
                  ...configFields.housingCounselingNameOfTheHousingEducationProgram.inputProps,
                  required: !isHousingCounselingNameOfTheHousingEducationProgram,
                  disabled: isHousingCounselingNameOfTheHousingEducationProgram,
                },
              },
              configFields.dateOfCompletionHousingCounseling,
            ]),
          ],
        },
      ],
    };
  },
);
